import { useQuery } from "@tanstack/react-query";
import { supabasePublic } from "@/integrations/supabase/publicClient";
import type { ContestEntryField } from "@/lib/entryFields";
import { parseValidationRules } from "@/lib/entryFields";

export type { ContestEntryField } from "@/lib/entryFields";

export interface ContestTheme {
  headingColor?: string;
  buttonBackground?: string;
  buttonText?: string;
  background?: string;
}

export interface TermsVariables {
  sponsorName?: string;
  sponsorAddress?: string;
  eligibleRegions?: string;
  minimumAge?: string;
  excludedStates?: string;
  startDate?: string;
  startTime?: string;
  prizeDescription?: string;
  prizeArv?: string;
  prizeQuantity?: string;
  totalPrizeValue?: string;
  winnerNotificationMethod?: string;
  winnerNotificationTimeframe?: string;
  responseWindow?: string;
  winnerSelectionDate?: string;
  governingState?: string;
  supportEmail?: string;
  mailInAddress?: string;
  entryLimit?: string;
  includeMarketingOptIn?: string;
}

export interface ParticipantCustomResponse {
  id: string;
  participant_id: string;
  contest_id: string;
  field_key: string;
  field_value: string;
  created_at: string;
}

export interface Participant {
  id: string;
  contest_id: string;
  email: string | null;
  referral_code: string;
  name?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_zip?: string | null;
  address_country?: string | null;
  email_opt_in?: boolean;
  email_opt_in_at?: string | null;
  sms_opt_in?: boolean;
  sms_opt_in_at?: string | null;
  created_at: string;
}

export interface Contest {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  description_html: string | null;
  image_url: string | null;
  logo_url: string | null;
  hero_image_url: string | null;
  hero_image_aspect: string | null;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  ended_at: string | null;
  referral_bonus_entries: number;
  theme_json: ContestTheme | null;
  terms_template_version: string | null;
  terms_variables_json: TermsVariables | null;
  use_custom_terms?: boolean;
  custom_terms_text?: string | null;
  require_terms_acceptance?: boolean;
  hide_powered_by_branding?: boolean;
  show_help_email?: boolean;
  help_email?: string | null;
  show_total_entries?: boolean;
  initial_form_counts_as_entry?: boolean;
  created_at: string;
  updated_at: string;
  organization_id: string;
  legal_privacy_url?: string | null;
  legal_terms_url?: string | null;
  email_consent_helper?: string | null;
  org_email_consent_helper?: string | null;
  show_entry_methods_heading?: boolean;
  entry_methods_heading?: string | null;
  entry_fields?: ContestEntryField[];
}

export interface EntryMethod {
  id: string;
  contest_id: string;
  key: string;
  label: string;
  description: string | null;
  entries_awarded: number;
  max_per_device_per_day: number;
  max_per_device_total: number | null;
  is_enabled: boolean;
  sort_order: number;
  action_url: string | null;
  icon_type: string | null;
  icon_key: string | null;
  icon_url: string | null;
  created_at: string;
}

export function useContest(slug: string) {
  return useQuery({
    queryKey: ["contest", slug],
    queryFn: async (): Promise<{ contest: Contest; entryMethods: EntryMethod[]; totalEntries: number | null }> => {
      // Fetch contest using public client so auth state doesn't filter by org
      const { data: contest, error: contestError } = await supabasePublic
        .from("contests")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .maybeSingle();

      if (contestError) throw contestError;
      if (!contest) throw new Error("Contest not found");

      if (!(contest as Contest).email_consent_helper) {
        const { data: orgRow, error: orgError } = await supabasePublic
          .from("organizations")
          .select("email_consent_helper")
          .eq("id", contest.organization_id)
          .maybeSingle();
        if (!orgError && orgRow) {
          (contest as Contest).org_email_consent_helper = orgRow.email_consent_helper;
        }
      }

      // Fetch entry methods
      const { data: entryMethods, error: methodsError } = await supabasePublic
        .from("entry_methods")
        .select("*")
        .eq("contest_id", contest.id)
        .eq("is_enabled", true)
        .order("sort_order", { ascending: true });

      if (methodsError) throw methodsError;

      // Fetch entry fields
      let { data: entryFields, error: entryFieldsError } = await supabasePublic
        .from("contest_entry_fields")
        .select("id, field_type, field_key, label, placeholder, is_required, is_enabled, sort_order, validation_rules")
        .eq("contest_id", contest.id)
        .eq("is_enabled", true)
        .order("sort_order", { ascending: true });

      if (entryFieldsError) {
        const retry = await supabasePublic
          .from("contest_entry_fields")
          .select("id, field_type, field_key, label, placeholder, is_required, is_enabled, sort_order")
          .eq("contest_id", contest.id)
          .eq("is_enabled", true)
          .order("sort_order", { ascending: true });
        entryFields = retry.data as typeof entryFields;
        entryFieldsError = retry.error;
      }

      if (entryFieldsError) {
        console.error("Failed to fetch entry fields:", entryFieldsError);
      }

      (contest as Contest).entry_fields = (entryFields ?? []).map((field) => ({
        ...field,
        field_type: field.field_type as ContestEntryField["field_type"],
        is_required: !!field.is_required,
        is_enabled: field.is_enabled ?? true,
        sort_order: field.sort_order ?? 0,
        validation_rules: parseValidationRules((field as { validation_rules?: unknown }).validation_rules),
      }));

      // Fetch signed assets from Edge Function
      try {
        const { data: assets, error: assetsError } = await supabasePublic.functions.invoke('public-contest-assets', {
          body: { slug }
        });

        if (!assetsError && assets) {
          if (assets.logoUrl) contest.logo_url = assets.logoUrl;
          if (assets.heroUrl) contest.hero_image_url = assets.heroUrl;

          if (assets.entryIcons && entryMethods) {
            entryMethods.forEach((m: any) => {
              if (assets.entryIcons[m.id]) {
                m.icon_url = assets.entryIcons[m.id];
              }
            });
          }
        }
      } catch (err) {
        console.error("Failed to load assets:", err);
        // Non-critical, fall back to DB values (which might be null or old URLs)
      }

      let totalEntries: number | null = null;
      if ((contest as Contest).show_total_entries) {
        const { count } = await supabasePublic
          .from('entries')
          .select('*', { count: 'exact', head: true })
          .eq('contest_id', contest.id);
        totalEntries = count;
      }

      return {
        contest: contest as Contest,
        entryMethods: (entryMethods || []) as EntryMethod[],
        totalEntries,
      };
    },
    enabled: !!slug,
    staleTime: 1000 * 60, // 1 minute
  });
}
